import React, { useState, useCallback } from 'react';
import { MapPin, Phone, Clock, Factory, Send, CheckCircle } from 'lucide-react';

// Formspree configuration
const FORMSPREE_ID = 'xvzvrzar';
const FORMSPREE_URL = `https://formspree.io/f/${FORMSPREE_ID}`;

// Constantes
const FORM_SUBJECTS = [
  'Demande commerciale',
  'Information produit',
  'Support technique',
  'Demande de visite',
  'Autre'
];

const CONTACT_INFO = [
  {
    icon: Factory,
    gradient: '#0A2E5C',
    title: 'Nitrokym',
    getContent: (usine) => ({
      items: [
        { label: 'Nom', value: usine.nom },
        { label: 'Description', value: usine.description }
      ]
    })
  },
  {
    icon: MapPin,
    gradient: '#28a745',
    title: 'Adresse',
    getContent: (usine) => ({
      items: [
        { label: 'Adresse', value: usine.adresse },
        { label: 'Ville', value: usine.ville }
      ]
    })
  },
  {
    icon: Phone,
    gradient: '#FF6B35',
    title: 'Contact',
    getContent: (usine) => ({
      items: [
        { label: 'Téléphone', value: usine.telephone },
        { label: 'Email général', value: usine.email },
        { label: 'Commercial', value: usine.commercial }
      ]
    })
  },
  {
    icon: Clock,
    gradient: '#6f42c1',
    title: 'Horaires',
    getContent: () => ({
      items: [
        { label: 'Horaires d\'ouverture', value: 'Lundi - Vendredi : 8h00 - 16h30\nSamedi : Fermé\nDimanche : Fermé' }
      ]
    })
  }
];

const COUNTRIES = [
  { name: 'Afghanistan', prefix: '+93' },
  { name: 'Allemagne', prefix: '+49' },
  { name: 'Algérie', prefix: '+213' },
  { name: 'Arabie Saoudite', prefix: '+966' },
  { name: 'Argentine', prefix: '+54' },
  { name: 'Australie', prefix: '+61' },
  { name: 'Bahreïn', prefix: '+973' },
  { name: 'Bangladesh', prefix: '+880' },
  { name: 'Belgique', prefix: '+32' },
  { name: 'Bhoutan', prefix: '+975' },
  { name: 'Birmanie', prefix: '+95' },
  { name: 'Bolivie', prefix: '+591' },
  { name: 'Brésil', prefix: '+55' },
  { name: 'Cambodge', prefix: '+855' },
  { name: 'Canada', prefix: '+1' },
  { name: 'Chili', prefix: '+56' },
  { name: 'Chine', prefix: '+86' },
  { name: 'Chypre', prefix: '+357' },
  { name: 'Colombie', prefix: '+57' },
  { name: 'Corée du Nord', prefix: '+850' },
  { name: 'Corée du Sud', prefix: '+82' },
  { name: 'Égypte', prefix: '+20' },
  { name: 'Émirats Arabes Unis', prefix: '+971' },
  { name: 'Équateur', prefix: '+593' },
  { name: 'Espagne', prefix: '+34' },
  { name: 'États-Unis', prefix: '+1' },
  { name: 'Fidji', prefix: '+679' },
  { name: 'France', prefix: '+33' },
  { name: 'Grèce', prefix: '+30' },
  { name: 'Guyana', prefix: '+592' },
  { name: 'Guyane Française', prefix: '+594' },
  { name: 'Hong Kong', prefix: '+852' },
  { name: 'Inde', prefix: '+91' },
  { name: 'Indonésie', prefix: '+62' },
  { name: 'Iran', prefix: '+98' },
  { name: 'Irak', prefix: '+964' },
  { name: 'Italie', prefix: '+39' },
  { name: 'Japon', prefix: '+81' },
  { name: 'Jordanie', prefix: '+962' },
  { name: 'Kiribati', prefix: '+686' },
  { name: 'Koweït', prefix: '+965' },
  { name: 'Laos', prefix: '+856' },
  { name: 'Liban', prefix: '+961' },
  { name: 'Libye', prefix: '+218' },
  { name: 'Macao', prefix: '+853' },
  { name: 'Malaisie', prefix: '+60' },
  { name: 'Maldives', prefix: '+960' },
  { name: 'Malte', prefix: '+356' },
  { name: 'Maroc', prefix: '+212' },
  { name: 'Mexique', prefix: '+52' },
  { name: 'Micronésie', prefix: '+691' },
  { name: 'Nauru', prefix: '+674' },
  { name: 'Népal', prefix: '+977' },
  { name: 'Nouvelle-Zélande', prefix: '+64' },
  { name: 'Oman', prefix: '+968' },
  { name: 'palestine', prefix: '+970' },
  { name: 'Pakistan', prefix: '+92' },
  { name: 'Palaos', prefix: '+680' },
  { name: 'Papouasie-Nouvelle-Guinée', prefix: '+675' },
  { name: 'Paraguay', prefix: '+595' },
  { name: 'Pérou', prefix: '+51' },
  { name: 'Philippines', prefix: '+63' },
  { name: 'Portugal', prefix: '+351' },
  { name: 'Qatar', prefix: '+974' },
  { name: 'Royaume-Uni', prefix: '+44' },
  { name: 'Singapour', prefix: '+65' },
  { name: 'Sri Lanka', prefix: '+94' },
  { name: 'Suriname', prefix: '+597' },
  { name: 'Suisse', prefix: '+41' },
  { name: 'Syrie', prefix: '+963' },
  { name: 'Taïwan', prefix: '+886' },
  { name: 'Thaïlande', prefix: '+66' },
  { name: 'Tonga', prefix: '+676' },
  { name: 'Tunisie', prefix: '+216' },
  { name: 'Turquie', prefix: '+90' },
  { name: 'Tuvalu', prefix: '+688' },
  { name: 'Uruguay', prefix: '+598' },
  { name: 'Vanuatu', prefix: '+678' },
  { name: 'Venezuela', prefix: '+58' },
  { name: 'Vietnam', prefix: '+84' },
  { name: 'Yémen', prefix: '+967' },
  { name: 'Îles Marshall', prefix: '+692' },
  { name: 'Îles Salomon', prefix: '+677' }
];

// Validation regex
const VALIDATION_RULES = {
  nom: {
    pattern: /^[a-zA-ZÀ-ÿ\s'-]+$/,
    minLength: 3,
    required: true
  },
  email: {
    pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    required: true
  },
  telephone: {
    required: true,
    exactDigits: 8
  },
  sujet: {
    required: true
  },
  message: {
    minLength: 10,
    maxLength: 1000,
    required: true
  }
};

const Contact = ({ usine }) => {
  const [formData, setFormData] = useState({
    nom: '',
    email: '',
    telephone: '',
    pays: 'Tunisie',
    sujet: '',
    message: ''
  });

  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState({});
  const [phonePrefix, setPhonePrefix] = useState('+216');

  // Validation des champs
  const validateForm = useCallback(() => {
    const errors = {};

    // Nom
    if (!formData.nom.trim()) {
      errors.nom = 'Le nom est requis';
    } else if (formData.nom.trim().length < VALIDATION_RULES.nom.minLength) {
      errors.nom = `Le nom doit contenir au moins ${VALIDATION_RULES.nom.minLength} caractères`;
    } else if (!VALIDATION_RULES.nom.pattern.test(formData.nom)) {
      errors.nom = 'Le nom ne doit contenir que des lettres';
    }

    // Email
    if (!formData.email.trim()) {
      errors.email = 'L\'email est requis';
    } else if (!VALIDATION_RULES.email.pattern.test(formData.email)) {
      errors.email = 'Veuillez entrer un email valide';
    }

    // Pays
    if (!formData.pays || !formData.pays.trim()) {
      errors.pays = 'Veuillez sélectionner un pays';
    }

    // Téléphone
    if (!formData.telephone.trim()) {
      errors.telephone = 'Le téléphone est requis';
    }

    // Sujet
    if (!formData.sujet.trim()) {
      errors.sujet = 'Veuillez sélectionner un sujet';
    }

    // Message
    if (!formData.message.trim()) {
      errors.message = 'Le message est requis';
    } else if (formData.message.trim().length < VALIDATION_RULES.message.minLength) {
      errors.message = `Le message doit contenir au moins ${VALIDATION_RULES.message.minLength} caractères`;
    } else if (formData.message.trim().length > VALIDATION_RULES.message.maxLength) {
      errors.message = `Le message ne doit pas dépasser ${VALIDATION_RULES.message.maxLength} caractères`;
    }

    return errors;
  }, [formData]);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;

    if (name === 'pays') {
      const selectedCountry = COUNTRIES.find(country => country.name === value);
      setPhonePrefix(selectedCountry?.prefix || '');
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
      return;
    }

    // Filtrage spécifique au téléphone
    const updatedValue = name === 'telephone' 
      ? value.replace(/[^\d\s+\-()]/g, '') 
      : value;

    setFormData(prev => ({
      ...prev,
      [name]: updatedValue
    }));

    // Effacer l'erreur associée
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  }, [validationErrors]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const errors = validateForm();
    
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      setError('Veuillez corriger les erreurs du formulaire');
      return;
    }

    setValidationErrors({});
    setLoading(true);
    setError('');

    const submissionData = {
      nom: formData.nom,
      email: formData.email,
      telephone: `${phonePrefix} ${formData.telephone}`.trim(),
      pays: formData.pays,
      sujet: formData.sujet,
      message: formData.message,
      _subject: `Nouvelle demande de contact - ${formData.sujet}`,
      _captcha: false
    };

    try {
      const response = await fetch(FORMSPREE_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(submissionData)
      });

      if (process.env.NODE_ENV === 'development') {
        console.log('Formspree Response Status:', response.status);
      }
      const responseData = await response.json();
      if (process.env.NODE_ENV === 'development') {
        console.log('Formspree Response:', responseData);
      }

      if (!response.ok) {
        throw new Error(`Erreur Formspree: ${response.status} - ${responseData.message || 'Erreur inconnue'}`);
      }

      setSubmitted(true);
      setLoading(false);
      
      // Reset après 5 secondes
      const timer = setTimeout(() => {
        setFormData({
          nom: '',
          email: '',
          telephone: '',
          pays: 'Tunisie',
          sujet: '',
          message: ''
        });
        setSubmitted(false);
      }, 5000);

      return () => clearTimeout(timer);
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Erreur Formspree détaillée:', err);
        console.error('Message:', err.message);
      }
      setError(`Erreur : ${err.message}. Veuillez vérifier votre connexion et réessayer ou nous appeler directement.`);
      setLoading(false);
    }
  };

  return (
    <div className="contact">
      {/* Header */}
      <HeroHeader />

      {/* Coordonnées */}
      <CoordinatesSection usine={usine} />

      {/* Carte */}
      <MapSection />

      {/* Formulaire Contact */}
      <FormSection 
        submitted={submitted}
        setSubmitted={setSubmitted}
        formData={formData}
        loading={loading}
        error={error}
        validationErrors={validationErrors}
        handleChange={handleChange}
        handleSubmit={handleSubmit}
        phonePrefix={phonePrefix}
      />

      {/* Urgence */}
      <UrgencySection />
    </div>
  );
};

// Composants extraits
const HeroHeader = () => (
  <section className="hero" style={{
    backgroundImage: 'linear-gradient(rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.2)), url(/images/contact.png)',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundAttachment: 'fixed',
    position: 'relative'
  }}>
    <div className="container" style={{ position: 'relative', zIndex: 2 }}>
      <h1 className="hero-title">Contactez Nitrokym</h1>
      <p className="hero-subtitle">
        Notre équipe est à votre écoute pour répondre à toutes vos questions
      </p>
    </div>
  </section>
);

const ContactInfoCard = ({ item, usine }) => {
  const Icon = item.icon;
  const content = item.getContent(usine);

  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '20px' }}>
      <div style={{ 
        width: '60px', 
        height: '60px', 
        background: item.gradient, 
        borderRadius: '12px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white',
        flexShrink: 0
      }}>
        <Icon size={28} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#0A2E5C', marginBottom: '8px' }}>
          {item.title}
        </h3>
        <div style={{ color: '#666', lineHeight: '1.6' }}>
          {content.items.map((infoItem, idx) => (
            <div key={idx} style={{ marginBottom: '8px' }}>
              <strong style={{ color: '#0A2E5C' }}>{infoItem.label}:</strong> <span style={{ whiteSpace: infoItem.value.includes('\n') ? 'pre-line' : 'nowrap', wordWrap: 'break-word', overflowWrap: 'break-word' }}>{infoItem.value}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const CoordinatesSection = ({ usine }) => (
  <section className="section" style={{ background: 'linear-gradient(135deg, #f8f9fa 0%, #f0f4f8 100%)' }}>
    <div className="container">
      <div style={{ textAlign: 'center', marginBottom: '60px' }}>
        <h2 className="section-title" style={{ color: '#0A2E5C', marginBottom: '15px' }}>Coordonnées de Nitrokym</h2>
        <p className="section-subtitle">Nous sommes là pour vous aider</p>
      </div>
      
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '30px', maxWidth: '1200px', margin: '0 auto' }}>
        {CONTACT_INFO.map((item, index) => (
          <div key={index} style={{
            background: 'white',
            padding: '45px',
            borderRadius: '16px',
            boxShadow: '0 5px 20px rgba(0, 0, 0, 0.08)',
            transition: 'all 0.3s ease',
            borderTop: '4px solid',
            borderTopColor: item.gradient
          }}>
            <ContactInfoCard item={item} usine={usine} />
          </div>
        ))}
      </div>
    </div>
  </section>
);

const MapSection = () => (
  <section className="section" style={{ background: 'linear-gradient(135deg, #f8f9fa 0%, #f0f4f8 100%)' }}>
    <div className="container">
      <h2 className="section-title">Localisation</h2>
      <p className="section-subtitle">Visitez-nous à Zaghouan, Tunisie</p>
      <div className="map-container" style={{ boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)', borderRadius: '12px', overflow: 'hidden' }}>
        <iframe
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3204.67947402523!2d10.07558377563365!3d36.561840672315995!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x12fd238bf5a65997%3A0x5c5f362de9501230!2sSociet%C3%A9%20Nitrokym!5e0!3m2!1sfr!2stn!4v1774277795024!5m2!1sfr!2stn"
          width="100%"
          height="450"
          style={{ border: 0, display: 'block' }}
          allowFullScreen=""
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          title="Localisation Société Nitrokym"
        ></iframe>
      </div>
    </div>
  </section>
);

const FormSection = ({ submitted, setSubmitted, formData, loading, error, validationErrors, handleChange, handleSubmit, phonePrefix }) => (
  <section className="section">
    <div className="container">
      {submitted ? (
        <SuccessMessage formData={formData} setSubmitted={setSubmitted} />
      ) : (
        <ContactForm 
          formData={formData}
          loading={loading}
          error={error}
          validationErrors={validationErrors}
          handleChange={handleChange}
          handleSubmit={handleSubmit}
          phonePrefix={phonePrefix}
        />
      )}
    </div>
  </section>
);

const SuccessMessage = ({ formData, setSubmitted }) => (
  <div style={{
    maxWidth: '800px',
    margin: '0 auto',
    backgroundColor: 'white',
    borderRadius: '16px',
    padding: '50px 40px',
    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
    textAlign: 'center'
  }}>
    <div style={{ 
      width: '100px', 
      height: '100px', 
      background: 'linear-gradient(135deg, #28a745, #20c997)',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 auto 30px',
      boxShadow: '0 8px 25px rgba(40, 167, 69, 0.3)',
      animation: 'successPulse 2s infinite'
    }}>
      <CheckCircle size={50} color="white" />
    </div>
    <h2 style={{ color: '#0A2E5C', marginBottom: '15px', fontSize: '32px', fontWeight: '700' }}>Message Envoyé avec Succès !</h2>
    <p style={{ color: '#666', fontSize: '18px', marginBottom: '10px' }}>Merci <strong style={{ color: '#0A2E5C' }}>{formData.nom}</strong> de contacter Nitrokym.</p>
    <p style={{ color: '#666', fontSize: '16px', marginBottom: '30px' }}>Notre équipe vous répondra dans les plus brefs délais à :<br /><span style={{ color: '#FF6B35', fontWeight: '600', fontSize: '18px' }}>{formData.email}</span></p>
    
    <div style={{ 
      background: 'linear-gradient(135deg, #e8f5e8, #f0f9f0)', 
      padding: '25px', 
      borderRadius: '12px',
      margin: '30px 0',
      border: '1px solid #d4edda'
    }}>
      <p style={{ color: '#28a745', fontSize: '14px', margin: 0 }}> <strong>Promesse Nitrokym :</strong> Traitement confidentiel • Réponse sous 48h ouvrées • Support technique inclus</p>
    </div>

    <button 
      onClick={() => setSubmitted(false)}
      style={{
        padding: '14px 30px',
        backgroundColor: '#0A2E5C',
        color: 'white',
        border: 'none',
        borderRadius: '8px',
        fontWeight: '600',
        fontSize: '16px',
        cursor: 'pointer',
        marginTop: '30px',
        transition: 'all 0.3s ease',
        boxShadow: '0 5px 15px rgba(10, 46, 92, 0.3)'
      }}
      onMouseEnter={(e) => {
        e.target.style.backgroundColor = '#FF6B35';
        e.target.style.transform = 'translateY(-2px)';
        e.target.style.boxShadow = '0 8px 25px rgba(255, 107, 53, 0.3)';
      }}
      onMouseLeave={(e) => {
        e.target.style.backgroundColor = '#0A2E5C';
        e.target.style.transform = 'translateY(0)';
        e.target.style.boxShadow = '0 5px 15px rgba(10, 46, 92, 0.3)';
      }}
    >
      Envoyer un nouveau message
    </button>
  </div>
);

const ContactForm = ({ formData, loading, error, validationErrors, handleChange, handleSubmit, phonePrefix }) => (
  <form onSubmit={handleSubmit} className="form-container" style={{
    maxWidth: '900px',
    margin: '0 auto',
    backgroundColor: 'white',
    borderRadius: '16px',
    padding: '50px 40px',
    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)'
  }}>
    <div style={{ textAlign: 'center', marginBottom: '40px' }}>
      <h2 style={{ color: '#0A2E5C', fontSize: '32px', fontWeight: '700', marginBottom: '10px' }}>Formulaire de Contact</h2>
      <p style={{ color: '#666', fontSize: '16px' }}>Remplissez ce formulaire pour nous contacter</p>
    </div>

    {error && <ErrorAlert message={error} />}

    <FormRow>
      <FormField
        label="Nom complet *"
        name="nom"
        type="text"
        value={formData.nom}
        onChange={handleChange}
        error={validationErrors.nom}
        placeholder="Votre nom et prénom"
      />
      <FormField
        label="Email *"
        name="email"
        type="email"
        value={formData.email}
        onChange={handleChange}
        error={validationErrors.email}
        placeholder="nom@exemple.com"
      />
    </FormRow>

    <FormRow>
      <FormField
        label="Pays *"
        name="pays"
        type="select"
        value={formData.pays}
        onChange={handleChange}
        error={validationErrors.pays}
        options={COUNTRIES
          .slice()
          .sort((a, b) => a.name.localeCompare(b.name, 'fr', { sensitivity: 'base' }))
          .map(country => country.name)
        }
        placeholder="Sélectionnez un pays"
      />
    </FormRow>

    <FormRow>
      <PhoneField
        value={formData.telephone}
        onChange={handleChange}
        error={validationErrors.telephone}
        prefix={phonePrefix}
      />
      <FormField
        label="Sujet *"
        name="sujet"
        type="select"
        value={formData.sujet}
        onChange={handleChange}
        error={validationErrors.sujet}
        options={FORM_SUBJECTS}
      />
    </FormRow>

    <FormField
      label="Message *"
      name="message"
      type="textarea"
      value={formData.message}
      onChange={handleChange}
      error={validationErrors.message}
      placeholder="Votre message..."
      maxLength={1000}
      rows={5}
    />

    <InfoBox />

    <SubmitButton loading={loading} />
  </form>
);

// Sous-composants de formulaire
const FormRow = ({ children }) => (
  <div className="form-row">
    {children}
  </div>
);

const FormField = ({ label, name, type, value, onChange, error, placeholder, options, maxLength, rows }) => (
  <div className="form-group">
    <label className="form-label">{label}</label>
    {type === 'select' ? (
      <select
        name={name}
        value={value}
        onChange={onChange}
        className="form-select"
        style={{ borderColor: error ? '#dc3545' : '' }}
      >
        <option value="">{placeholder || 'Sélectionnez une option'}</option>
        {options?.map(opt => (
          <option key={opt} value={opt}>{opt}</option>
        ))}
      </select>
    ) : type === 'textarea' ? (
      <>
        <textarea
          name={name}
          value={value}
          onChange={onChange}
          className="form-textarea"
          placeholder={placeholder}
          rows={rows}
          maxLength={maxLength}
          style={{ borderColor: error ? '#dc3545' : '' }}
        />
        <small style={{ color: '#666', fontSize: '12px', marginTop: '5px', display: 'block' }}>
          {value.length}/{maxLength}
        </small>
      </>
    ) : (
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        className="form-input"
        placeholder={placeholder}
        style={{ borderColor: error ? '#dc3545' : '' }}
      />
    )}
    {error && <ErrorMessage message={error} />}
  </div>
);

const PhoneField = ({ value, onChange, error, prefix }) => {
  const digitCount = value.replace(/\D/g, '').length;

  return (
    <div className="form-group">
      <label className="form-label">
        Téléphone *
      </label>
      <div style={{ display: 'flex', alignItems: 'stretch', gap: '0' }}>
        <div style={{
          background: '#f8f9fa',
          border: '1px solid #ced4da',
          borderRight: 'none',
          borderRadius: '8px 0 0 8px',
          minWidth: '95px',
          height: '50px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: '600',
          color: '#495057'
        }}>
          {prefix || '+216'}
        </div>
        <input
          type="tel"
          name="telephone"
          value={value}
          onChange={onChange}
          className="form-input"
          placeholder="votre numéro de téléphone"
          maxLength="12"
          style={{
            borderColor: error ? '#dc3545' : '',
            borderRadius: '0 8px 8px 0',
            borderLeft: 'none',
            flex: '1',
            minWidth: 0,
            height: '50px',
            padding: '0 12px',
            boxSizing: 'border-box'
          }}
        />
      </div>
      <small style={{ color: '#666', fontSize: '12px', marginTop: '5px', display: 'block' }}>
        {digitCount} chiffres
      </small>
      {error && <ErrorMessage message={error} />}
    </div>
  );
};

const ErrorAlert = ({ message }) => (
  <div style={{ 
    background: '#f8d7da', 
    color: '#721c24', 
    padding: '15px',
    borderRadius: '8px',
    marginBottom: '20px',
    border: '1px solid #f5c6cb'
  }}>
    {message}
  </div>
);

const ErrorMessage = ({ message }) => (
  <span style={{ color: '#dc3545', fontSize: '14px', marginTop: '5px', display: 'block' }}>
    {message}
  </span>
);

const InfoBox = () => (
  <div style={{ 
    background: 'linear-gradient(135deg, #e8f4f8, #f0f9ff)', 
    padding: '25px',
    borderRadius: '12px',
    margin: '30px 0',
    border: '1px solid #d1e8f0',
    boxShadow: '0 4px 15px rgba(0, 0, 0, 0.05)'
  }}>
    <p style={{ color: '#0A2E5C', fontSize: '15px', textAlign: 'center', margin: 0 }}>
      <strong> Engagement Nitrokym :</strong> Traitement confidentiel • Réponse sous 48h ouvrées • Support technique inclus
    </p>
  </div>
);

const SubmitButton = ({ loading }) => (
  <div style={{ textAlign: 'center', marginTop: '40px' }}>
    <button 
      type="submit" 
      disabled={loading}
      style={{
        padding: '18px 40px',
        backgroundColor: loading ? '#ccc' : '#0A2E5C',
        color: 'white',
        border: 'none',
        borderRadius: '10px',
        fontWeight: '700',
        fontSize: '18px',
        cursor: loading ? 'not-allowed' : 'pointer',
        transition: 'all 0.3s ease',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '10px',
        boxShadow: loading ? 'none' : '0 8px 25px rgba(10, 46, 92, 0.3)',
        textTransform: 'uppercase',
        letterSpacing: '0.5px'
      }}
      onMouseEnter={(e) => {
        if (!loading) {
          e.target.style.backgroundColor = '#FF6B35';
          e.target.style.transform = 'translateY(-3px)';
          e.target.style.boxShadow = '0 12px 35px rgba(255, 107, 53, 0.4)';
        }
      }}
      onMouseLeave={(e) => {
        if (!loading) {
          e.target.style.backgroundColor = '#0A2E5C';
          e.target.style.transform = 'translateY(0)';
          e.target.style.boxShadow = '0 8px 25px rgba(10, 46, 92, 0.3)';
        }
      }}
    >
      {loading ? (
        <span> Envoi en cours...</span>
      ) : (
        <>
          <Send size={22} />
          Envoyer le message
        </>
      )}
    </button>
  </div>
);

const UrgencySection = () => (
  <section className="hero" >
    <div className="container" style={{ textAlign: 'center' }}>
      <h2 className="hero-title" style={{ marginBottom: '15px' }}> Urgence Technique 24h/7j</h2>
      <p className="hero-subtitle">
        Pour les urgences techniques ou les demandes prioritaires concernant nos produits
      </p>
      <div style={{ 
        background: 'rgba(255,255,255,0.15)', 
        padding: '40px',
        borderRadius: '16px',
        maxWidth: '500px',
        margin: '40px auto 0',
        textAlign: 'center',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(255,255,255,0.2)',
        boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)'
      }}>
        <Phone size={50} color="#60a5fa" style={{ marginBottom: '20px' }} />
        <h3 style={{ color: 'white', marginBottom: '15px', fontSize: '24px', fontWeight: '700' }}>
          Support Technique
        </h3>
        <a
          href="tel:+21698765432"
          style={{
            color: '#dbeafe',
            fontSize: '22px',
            fontWeight: '700',
            marginBottom: '10px',
            textDecoration: 'none',
            display: 'inline-block',
            transition: 'color 0.3s ease'
          }}
          onMouseEnter={(e) => e.target.style.color = '#60a5fa'}
          onMouseLeave={(e) => e.target.style.color = '#dbeafe'}
        >
          +216 70 279 320
        </a>
        <p style={{ color: '#93c5fd', fontSize: '15px', marginTop: '15px' }}>
          ✓ Disponible 24h/24 - 7j/7<br/>
          ✓ Support technique expert<br/>
          ✓ Urgence production
        </p>
      </div>
    </div>
  </section>
);

export default Contact;